import { useEffect, useState } from 'react'

import { t } from '../copy'
import { fetchDeployedBuild, isStale, runningBuild } from './build-freshness'

/** 多久问一次「线上换版本了吗」。五分钟——它不是实时性要求，是别让人一天都不知道。 */
const POLL_MS = 5 * 60 * 1000

/**
 * 「这个页面是旧的一版」。
 *
 * # 它修的是什么
 *
 * 2026-09-07：一台手机上开着的标签页连续 19.5 小时跑着前一天的 bundle，
 * 其间上线过八次以上。**发布方说「已上线」，用户说「我这儿还是旧的」，
 * 两边都对**——而在这之前没有任何东西能让他们发现说的不是同一份。
 *
 * # 三条刻意的克制
 *
 *  1. **不自动刷新。** 他可能正在输入。给一句话加一个按钮，由他点。
 *  2. **不叫「错误」，也不说「请刷新」。** 他什么都没做错，
 *     也不该被指使去做一个运维动作——所以按钮写的是「换成新的」。
 *  3. **不确定就不出现。** 本地不知道自己是哪一版、服务端没回、
 *     回来的不是 SHA，一律当没过期（判据在 build-freshness.ts）。
 *     一条假提醒会让他刷新，刷完还是这一版，**那比不提醒更伤信任**。
 *
 * 除了定时问，**切回这个标签页时也问一次**：手机上最常见的形态正是
 * 「切走一整天，切回来接着用」，而那一刻恰好是它最可能已经旧了的时刻。
 */
export function StaleBuildNotice() {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let alive = true
    const check = async () => {
      const deployed = await fetchDeployedBuild()
      if (!alive) return
      setStale(isStale(runningBuild(), deployed))
    }
    void check()
    const timer = window.setInterval(() => void check(), POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      alive = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!stale) return null
  return (
    <div
      data-testid="stale-build-notice"
      role="status"
      className="flex items-center justify-between gap-npc-el border-b border-npc-line bg-npc-warm px-npc-card py-2 text-npc-caption text-npc-t2"
    >
      <span className="min-w-0 truncate">{t('ui.stale_build.notice')}</span>
      <button
        type="button"
        data-testid="stale-build-reload"
        onClick={() => window.location.reload()}
        className="shrink-0 rounded-npc-btn bg-npc-green px-3 py-1 text-npc-caption text-npc-panel"
      >
        {t('ui.stale_build.action')}
      </button>
    </div>
  )
}
